import {
  IonButton,
  IonCol,
  IonGrid,
  IonHeader,
  IonModal,
  IonRow,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { FC } from "react";
import { OrderPriceDetails } from "./OrderPriceDetails";
import { ICart, PaymentMethod } from "../../models";
import { Flex } from "../../components/Flex/Flex";
import InfoMessage from "../../components/InfoMessage/InfoMessage";
import { useCommon } from "../../common/useCommon";
import { BIH_PRICE_MESSAGE } from "../../common/constants";
import { NumberMoney } from "../../components/NumberMoney/NumberMoney";
import { SELLER_CARD, SELLER_COD } from "../../common/sellers";

const Row: FC<{ label: string; value?: string }> = ({ label, value }) => (
  <IonRow>
    <IonCol
      style={{
        textAlign: "right",
        color: "var(--ion-color-dark-shade)",
      }}
    >
      <span style={{ lineHeight: "25px" }}>{label}</span>
    </IonCol>
    <IonCol className="b vertical-center" style={{ minWidth: 0 }}>
      {/* A long email address breaks instead of widening the dialog on small phones. */}
      <span style={{ lineHeight: "25px", overflowWrap: "anywhere" }}>{value}</span>
    </IonCol>
  </IonRow>
);

interface Props {
  isOpen: boolean;
  cart: ICart;
  onCancel: () => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  paymentMethod?: PaymentMethod;
  /** Server message when a card order was refused. */
  error?: string | null;
  /** Amount charged by card, always RSD. */
  totalDin?: number;
}

export const OrderConfirmation: FC<Props> = ({
  isOpen,
  cart,
  onCancel,
  onSubmit,
  isSubmitting,
  paymentMethod = "COD",
  error,
  totalDin,
}) => {
  const isCard = paymentMethod === "CARD";
  const { currency } = useCommon();
  return (
    <IonModal isOpen={isOpen} onDidDismiss={onCancel}>
      <IonHeader>
        <IonToolbar color="success">
          <IonTitle>Potvrda porudžbine</IonTitle>
        </IonToolbar>
      </IonHeader>
      <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        <IonGrid style={{ width: "100%" }}>
          <Row label="Ime i prezime" value={cart.name} />
          <Row label="Adresa" value={cart.address} />
          <Row label="Poš. br. i mesto" value={`${cart.zip} ${cart.place}`} />
          <Row label="Telefon" value={cart.phone} />
          <Row label="e-mail" value={cart.email} />
          <Row label="Napomena" value={cart.note} />
          <Row
            label="Način plaćanja"
            value={isCard ? "Platnom karticom" : "Pouzećem"}
          />
          <Row
            label="Prodavac"
            value={`${(isCard ? SELLER_CARD : SELLER_COD).name}, PIB ${(isCard ? SELLER_CARD : SELLER_COD).pib}`}
          />
        </IonGrid>
        <OrderPriceDetails />
        <div
          style={{
            padding: "3px 10px 8px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <InfoMessage
            text={
              <>
                Molimo Vas da još jednom proverite da li ste ispravno upisali
                broj telefona.
                <br />
                <b>{cart.phone}</b>
              </>
            }
            level="warning"
          />
          {currency === "KM" && (
            <InfoMessage text={BIH_PRICE_MESSAGE} level="warning" />
          )}
          {isCard && (
            <InfoMessage
              text={
                <>
                  Klikom na PLATI KARTICOM prelazite na zaštićenu stranicu banke
                  za unos podataka o kartici.
                  {currency === "KM" && (
                    <>
                      <br />
                      Kartica se zadužuje u dinarima:{" "}
                      <b>
                        <NumberMoney value={totalDin ?? 0} forceCurrency="DIN" />
                      </b>
                    </>
                  )}
                </>
              }
            />
          )}
          {error && <InfoMessage text={error} level="danger" />}
        </div>
        {/* Wraps on narrow phones: the pay button then takes its own full-width line
            instead of being cut off at the right edge. */}
        <Flex css={{ padding: "0 10px", marginBottom: "8px", flexWrap: "wrap" }}>
          <IonButton
            color="light"
            onClick={onCancel}
            style={{ flexGrow: 0 }}
            disabled={isSubmitting}
          >
            Vrati se nazad
          </IonButton>
          <IonButton
            color="primary"
            onClick={onSubmit}
            style={{ flexGrow: 1 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <IonSpinner name="crescent" style={{ width: 21, height: 21 }} />
            ) : isCard ? (
              <>PLATI KARTICOM</>
            ) : (
              <>NARUČI</>
            )}
          </IonButton>
        </Flex>
      </div>
    </IonModal>
  );
};
